import { convertToFormSelect } from 'helpers';
import { showError } from 'helpers/handleError';
import { convertArrayToObject } from 'helpers/url';
import { isEmpty, isPlainObject } from 'lodash';
import { useState, useCallback, useRef, useLayoutEffect } from 'react';
import BackgroundProcessService from 'services/backgroundProcessServices';
import CacheService from 'services/CacheService';

//* GUIDE:
//**************************************
//* Check services of API              *
//* Check parseBodyToRequest function  *
//* Check checkConditionPass function  *
//**************************************

/**
 * @returns {{
 *  data: {
 *    serviceType: {},
 *    taskStatus: {},
 *    taskType: {},
 *    taskStatusDescription: []
 * },
 *  loading: boolean,
 *  refetching: boolean,
 *  error: string | any,
 *  refetch: () => Promise<void>,
 *  refetchWithLoading: () => Promise<void>,
 *  setData: Dispatch<SetStateAction<any[]>>,
 * }}
 */
const useGetBackgroundProcessEnums = ({ isTrigger = true } = {}) => {
  const controller = useRef(new AbortController());
  const [data, setData] = useState(CacheService.backgroundProcessEnum);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refetching, setRefetching] = useState(false);

  //! Function

  const checkConditionPass = useCallback((response) => {
    //* Check condition of response here to set data
    if (response?.data) {
      const resultParsed = isPlainObject(response?.data)
        ? {
            serviceType: convertArrayToObject(
              convertToFormSelect(response?.data?.serviceType),
              'label',
              'value'
            ),
            taskStatus: convertArrayToObject(
              convertToFormSelect(response?.data?.taskStatus),
              'label',
              'value'
            ),
            taskType: convertArrayToObject(
              convertToFormSelect(response?.data?.taskType),
              'label',
              'value'
            ),
            taskStatusDescription: response?.data?.taskStatusDescription,
          }
        : {};

      CacheService.set('backgroundProcessEnum', resultParsed);
      setData(resultParsed);
    }
  }, []);

  const fetch = useCallback(() => {
    if (!isTrigger || !isEmpty(CacheService.backgroundProcessEnum)) {
      return;
    }

    console.log('[TRIGGER]: useGetBackgroundProcessEnums()');
    return new Promise(async (resolve, reject) => {
      try {
        const response = await BackgroundProcessService.getBackgroundProcessEnums({
          signal: controller.current.signal,
        });

        resolve(response);
      } catch (error) {
        setError(error);
        reject(error);
      }
    });
  }, [isTrigger]);

  //* Refetch implicity (without changing loading state)
  const refetch = useCallback(async () => {
    try {
      setRefetching(true);
      const response = await fetch();
      checkConditionPass(response);
    } catch (error) {
      showError(error);
    } finally {
      setRefetching(false);
    }
  }, [fetch, checkConditionPass]);

  //* Refetch with changing loading state
  const refetchWithLoading = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch();
      checkConditionPass(response);
      setLoading(false);
    } catch (error) {
      if (!!error?.isCanceled) {
        return;
      }
      showError(error);
    } finally {
      setLoading(false);
    }
  }, [fetch, checkConditionPass]);

  useLayoutEffect(() => {
    let shouldSetData = true;
    controller.current = new AbortController();

    const fetchAPI = async () => {
      try {
        setLoading(true);
        const response = await fetch();
        if (shouldSetData) {
          checkConditionPass(response);
          setLoading(false);
        }
      } catch (error) {
        showError(error);
        setLoading(false);
      }
    };

    fetchAPI();

    return () => {
      controller.current.abort();
      shouldSetData = false;
    };
  }, [fetch]);

  return { data, loading, error, refetching, refetch, refetchWithLoading, setData };
};

export default useGetBackgroundProcessEnums;
